import { inject, Injectable } from '@angular/core';
import { gql } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { CheckStateEnum } from '@app/core/enums/check/check-state.enum';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Check, CheckInput } from '@app/core/model/entities/asset/check';
import { Document } from '@app/core/model/entities/document/document';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { DocumentsService } from '@app/features/main/views/organization-documents/documents.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, Observable, Subject } from 'rxjs';
import { map, reduce, tap } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class ChecksService {
  private apiService = inject(ApiService);
  private appManager = inject(AppManager);
  private documentsService = inject(DocumentsService);
  private assetsService = inject(AssetsService);
  private appConfig = inject(AppConfig);
  private sidePanelToggleSubject = new Subject<Check | null>();

  private addCheckSubject = new Subject<Check>();
  private deleteChecksSubject = new Subject<Check[]>();
  private updateCheckSubject = new Subject<Check>();
  private renewCheckSubject = new Subject<{ oldCheck: Check, renewedCheck: Check }>();

  private readonly checkInfoGraphqlFragment = gql`
    fragment CheckInfo on Check {
      id
      type
      assetId
      spaceIdsList
      state
      properties
      computedProperties
      creationDate
      creationActor {
        actorId
        name
      }
      lastChangeDate
      lastChangeActor {
        actorId
        name
      }
      dataDate
    }
  `;

  /**
   * Loads a checks page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch checks based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the checks.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Check objects, each with additional user information.
   */
  public loadChecksPage(selectParams: SelectParams): Observable<Check[]> {
    const query = gql`
      query ChecksBySelectParams($organizationId: String!, $selectParams: SelectParams!) {
        checks(organizationId: $organizationId, selectParams: $selectParams) {
          ...CheckInfo
        }
      }
      ${this.checkInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Check, data['checks'] as Check[])));
  }

  /**
   * Loads all checks pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch checks based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the checks.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Check objects, each with additional user information.
   */
  public loadAllChecks(selectParams: SelectParams): Observable<Check[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadChecksPage(params)
      .pipe(
        expand((checks, i) => {
          if (checks.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadChecksPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Check[])
      );
  }

  /**
   * Emits a Check once it has been created.
   */
  public get checkAdded$(): Observable<Check> {
    return this.addCheckSubject.asObservable();
  }

  /**
   * Emits a Check whenever it has been updated.
   */
  public get checkUpdated$(): Observable<Check> {
    return this.updateCheckSubject.asObservable();
  }

  /**
   * Emits whenever a Check is renewed.
   */
  public get checkRenewed$(): Observable<{ oldCheck: Check, renewedCheck: Check }> {
    return this.renewCheckSubject.asObservable();
  }

  /**
   * Emits as list of Checks after they have been deleted.
   */
  public get checksDeleted$(): Observable<Check[]> {
    return this.deleteChecksSubject.asObservable();
  }

  /**
   * Emits a Check whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<Check | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Call the API to create a new Check for an Asset.
   * @param checkInput Data for creating a check.
   * @return Created Asset.
   */
  public createCheck(checkInput: CheckInput): Observable<Check> {
    const mutation = gql`
      mutation CreateCheck($checkInput: CreateCheckInput!) {
        createCheck(checkInput: $checkInput) {
          ...CheckInfo
        }
      }
      ${this.checkInfoGraphqlFragment}
    `;
    const variables = {checkInput};
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Check, data['createCheck'])),
        tap(check => this.addCheckSubject.next(check)),
      );
  }

  /**
   * Call the API to update a Check.
   * @param check Check to update.
   * @param checkInput Data for updating the Check.
   * @return Updated Check.
   */
  public updateCheck(check: Check, checkInput: CheckInput): Observable<Check> {
    const mutation = gql`
      mutation UpdateCheck($checkInput: UpdateCheckInput!) {
        updateCheck(checkInput: $checkInput) {
          ...CheckInfo
        }
      }
      ${this.checkInfoGraphqlFragment}
    `;
    const variables = {
      checkInput: {
        id: check.id,
        spaceIds: check.spaceIds,
        ...checkInput
      }
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Check, data['updateCheck'])),
        tap(updatedCheck => this.updateCheckSubject.next(updatedCheck))
      );
  }

  /**
   * Call the API to renew an existing Check.
   * @param checkToRenew Check to be renewed.
   * @param spaceIds spaces for the new Check.
   * @return Observable that will emit the new Check once created.
   */
  public renewCheck(checkToRenew: Check, spaceIds: string[]): Observable<Check> {
    const mutation = gql`
      mutation RenewCheck($oldCheckId: String!, $checkInput: CreateCheckInput!) {
        renewCheck(id: $oldCheckId, checkInput: $checkInput) {
          ...CheckInfo
        }
      }
      ${this.checkInfoGraphqlFragment}
    `;
    const variables = {
      oldCheckId: checkToRenew.id,
      checkInput: {
        type: checkToRenew.type,
        assetId: checkToRenew.assetId,
        spaceIds: spaceIds
      }
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Check, data['renewCheck'])),
        tap(renewedCheck => {
          checkToRenew.state = CheckStateEnum.CHECK_CLOSED;
          this.renewCheckSubject.next({oldCheck: checkToRenew, renewedCheck: renewedCheck});
        })
      );
  }

  /**
   * Call the API to delete the given Checks.
   * @param checks List of Checks to delete.
   * @return Observable emitting true if all the checks are deleted, otherwise false.
   */
  public deleteChecks(checks: Check[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteChecks($checkIds: [String!]!) {
        deleteChecks(ids: $checkIds)
      }
    `;
    const variables = {
      checkIds: checks.map((check: Check) => check.id)
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteChecks'] as boolean),
        tap(success => success && this.deleteChecksSubject.next(checks))
      );
  }

  /**
   * Navigate to the checks tab of the Asset's sheet.
   * @param assetId ID of the Asset to navigate to.
   */
  public async navigateToAssetSheet(assetId: string): Promise<void> {
    await this.assetsService.navigateToAssetSheet(assetId, 'checks');
  }

  /**
   * Load all the Documents related to a given Check.
   * @param checkId id of the selected Check.
   * @return Observable emitting Documents linked to the Check.
   */
  public loadCheckDocuments(checkId: string): Observable<Document[]> {
    return this.documentsService.loadEntityDocuments(
      checkId,
      EntityTypeEnum.CHECK,
      DocumentType.DOCUMENT
    );
  }

  /**
   * Open the side panel with information about the selected Check.
   * @param check Check to be displayed in the sidenav.
   */
  public openCheckSidePanel(check: Check): void {
    this.sidePanelToggleSubject.next(check);
  }

  /**
   * Close the side panel.
   */
  public closeCheckSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }
}
